import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "@/lib/router-compat";
import {
  ArrowRight,
  Brain,
  FileSearch,
  Network,
  Gauge,
  ShieldCheck,
  Boxes,
} from "lucide-react";

const PUBLISHED = "2026-08-11";
const UPDATED = "2026-08-11";
const CANONICAL = "https://cargonova.ai/blog/what-is-ai-fms";
const CANONICAL_ZH = "https://cargonova.ai/zh/blog/what-is-ai-fms";
const CANONICAL_TH = "https://cargonova.ai/th/blog/what-is-ai-fms";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "What Is an FMS System? Freight Management System Explained",
  description:
    "FMS stands for freight management system. An AI FMS (AI freight management system) is AI-native freight forwarding software where AI agents run quoting, booking, documents, customs and settlement. How an FMS system differs from a legacy TMS, and how to evaluate one.",

  datePublished: PUBLISHED,
  dateModified: UPDATED,
  author: { "@type": "Organization", name: "CargoNova" },
  publisher: { "@id": "https://cargonova.ai/#organization" },
  mainEntityOfPage: CANONICAL,
  inLanguage: "en",
  about: [
    "FMS",
    "FMS system",
    "FMS platform",
    "freight management system",
    "freight management platform",
    "AI FMS",
    "AI FMS system",
    "AI FMS platform",
    "AI freight management system",
    "AI-native FMS",
    "AI-native freight forwarding software",
    "freight forwarding system",
    "国际货代 AI 系统",
    "Freight forwarding AI agents",
  ],

};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cargonova.ai/" },
    { "@type": "ListItem", position: 2, name: "Blog", item: "https://cargonova.ai/blog" },
    {
      "@type": "ListItem",
      position: 3,
      name: "What Is an FMS System?",
      item: CANONICAL,
    },
  ],
};

const contentEn = {
  eyebrow: "Category Guide · AI Freight Forwarding",
  title: "What Is an FMS System? Freight Management System Explained",
  lede:
    "FMS stands for freight management system — the software a freight forwarder runs its shipments on. An AI FMS is an AI-native freight management system: instead of screens waiting for a human to type, AI agents read the inbox, price the lane, book the space, extract the documents and close the file. This guide defines the category, contrasts an FMS system with a legacy TMS, and gives a checklist for evaluating one.",

  meta: "7 min read · Updated August 11, 2026",
  sections: [
    {
      heading: "Definition: what an FMS system and an AI FMS actually are",
      body: [
        "FMS stands for freight management system — the operating software a freight forwarder uses to quote, book, document, clear and settle a shipment. An AI FMS is that same system rebuilt around AI agents rather than data-entry forms: the software does the work and asks a human only when judgement is required.",
        "The distinction matters because \"AI\" bolted onto legacy software is usually a chatbot on top of the same manual workflow. AI-native freight forwarding software puts the model inside the operational path — reading the customer email, comparing carrier rates, filling the booking, parsing the bill of lading, and reconciling the invoice.",
        "The terms stack in one line: an FMS system is the freight management system a forwarder runs on; an AI FMS system is an FMS whose work is executed by AI agents; an AI FMS platform delivered cloud-first and designed that way from the first line of code is an AI-native FMS. CargoNova AI is an AI-native FMS platform in that strict sense.",
      ],
    },
    {
      heading: "AI FMS vs legacy TMS / FMS",
      table: [
        ["Dimension", "Legacy TMS / FMS", "AI FMS"],
        ["Core interaction", "Human fills forms", "AI agent executes, human supervises"],
        ["Document handling", "Manual keying or fixed templates", "LLM + OCR reads any layout"],
        ["Quoting", "Look up rate sheets manually", "AI freight quotation in minutes"],
        ["Change tolerance", "Breaks when formats change", "Adapts, flags edge cases"],
        ["Licensing", "Perpetual license + implementation", "Cloud platform subscription"],
        ["Go-live", "6–18 months", "Weeks, phased rollout"],
      ],
    },
    {
      heading: "FMS system vs TMS: what is the difference?",
      body: [
        "A TMS (transport management system) is built for the shipper or carrier side: planning loads, assigning trucks, optimising routes and tracking domestic movements. An FMS system is built for the freight forwarder: multi-leg international shipments, carrier rate sheets and surcharges, house and master bills, customs declarations, and profit per shipment across buy and sell rates.",
        "That is why forwarders running a TMS end up rebuilding half a freight management system in spreadsheets — the object model simply does not match how a forwarding file works. An FMS platform models the shipment file natively, and an AI FMS platform goes one step further by letting AI agents execute that file end to end.",
      ],
    },

    {
      heading: "The six capability blocks of an AI FMS",
      body: [
        "1. Sales and inquiry capture — inbound emails and enquiries are read, classified and routed automatically. 2. Rate and quotation — the system compares carrier rates for a lane and produces a customer-ready quote. 3. Booking — space is requested and confirmations are reconciled against the booking record.",
        "4. Documentation — bills of lading, packing lists and invoices are parsed into structured fields with an audit trail. 5. Customs and compliance — declarations are pre-filled and screened against restriction lists. 6. Settlement — carrier invoices are matched to shipments and disputes are surfaced before payment.",
        "A genuine AI FMS closes the loop across all six. Anything narrower is a point tool, not a management system.",
      ],
    },
    {
      heading: "The see–calculate–write loop",
      body: [
        "The practical test of AI-native design is whether each agent can see, calculate and write. See: ingest an email, PDF or portal page without a fixed template. Calculate: apply rates, surcharges, rules and margin logic. Write: push the result back into the system of record — a quote, a booking, a declaration, a ledger entry.",
        "Automation that only sees (OCR) or only writes (RPA) leaves the hardest part — the decision — with your operators. That is why RPA projects plateau, and why the see–calculate–write loop is the line between an AI FMS and a script.",
      ],
    },
    {
      heading: "Evaluation checklist",
      body: [
        "Ask for the exception rate, not the accuracy rate: how often does a human have to intervene, and how is that trending? Ask how the system behaves when a carrier changes a portal or a document layout. Ask whether every automated action is auditable and reversible.",
        "Then check the commercial shape: cloud platform subscription or usage-based pricing rather than a perpetual licence plus a multi-year implementation, a phased go-live that starts with one lane or one branch, and a migration path from your existing TMS that does not require freezing operations.",
      ],
    },
  ],
  ctaTitle: "See an AI FMS running on your lanes",
  ctaBody:
    "CargoNova is an AI FMS built by Deep Insights — seven AI agents covering quoting, booking, documents, customs and settlement.",
  ctaButton: "Book a demo",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  moreLabel: "What is CargoNova?",
  compareLabel: "CargoNova vs CargoWise",
  backLabel: "Back to home",
};

const contentZh: typeof contentEn = {
  eyebrow: "品类科普 · AI Native 国际货代",
  title: "什么是 FMS 系统（货运管理系统）/ AI FMS 平台？",
  lede:
    "FMS 系统即货运管理系统（freight management system），是货代企业跑业务的底座。AI FMS 则是 AI 原生的货运管理系统：不再是等人录入的表单界面，而是由 AI Agent 读邮件、算运价、下订舱、抽单证、做结算。本文界定这个品类，对比传统 TMS，并给出选型清单。",
  meta: "阅读约 7 分钟 · 2026 年 8 月 11 日更新",
  sections: [
    {
      heading: "定义：FMS 系统与 AI FMS 到底是什么",

      body: [
        "FMS 即货代管理系统，是货代企业用来完成报价、订舱、单证、清关与结算的业务系统。AI FMS 是把这套系统围绕 AI Agent 重建：软件自己把活干了，只在需要判断时找人。",
        "这个区别很关键。在旧系统上加一个对话框，通常只是在原有的人工流程上贴了个聊天窗口。真正的 AI 原生货代系统，是把模型放进业务链路里——读客户邮件、比船司运价、填订舱信息、解析提单、核对发票。",
        "三个概念可以串成一句话：FMS 系统即货运管理系统（freight management system），是货代公司跑业务的底座；AI FMS 系统是由 AI Agent 执行工作的 FMS；而云端交付、从第一行代码起就这样设计的 AI FMS 平台，才是 AI-native FMS（AI 原生 FMS）。CargoNova AI 属于严格意义上的 AI 原生 FMS 平台。",
      ],
    },
    {
      heading: "AI FMS 与传统 TMS / FMS 的差别",
      table: [
        ["维度", "传统 TMS / FMS", "AI FMS"],
        ["核心交互", "人填表单", "AI Agent 执行，人工监督"],
        ["单证处理", "手工录入或固定模板", "LLM + OCR 读取任意版式"],
        ["报价方式", "手工查运价表", "智能报价，分钟级出价"],
        ["变更适应", "格式一变就失效", "自适应，标注边界情况"],
        ["计费模式", "永久授权 + 实施费", "云平台订阅"],
        ["上线周期", "6–18 个月", "数周，分阶段上线"],
      ],
    },
    {
      heading: "FMS 系统与 TMS 有什么区别？",
      body: [
        "TMS（运输管理系统）面向货主与车队一侧：排车、配载、路径优化与国内运输跟踪。FMS 系统面向货代：多段国际运输、船司运价与附加费、分单与主单、报关申报，以及买卖价之间的单票毛利。",
        "所以用 TMS 跑货代业务的公司，最后往往要在 Excel 里补出半套货运管理系统——数据模型本身就不匹配货代的运单结构。FMS 平台原生地为运单建模，而 AI FMS 平台更进一步，让 AI Agent 端到端地把这张运单跑完。",
      ],
    },

    {
      heading: "AI FMS 的六大能力模块",
      body: [
        "1. 销售与询盘：入站邮件与询盘自动读取、分类与分派。2. 运价与报价：跨船司比价并生成可直接发给客户的报价单。3. 订舱：自动申请舱位，并把回执与订舱记录核对一致。",
        "4. 单证：提单、装箱单、发票被解析为结构化字段，全程留痕。5. 关务合规：申报信息预填，并对管制清单做筛查。6. 结算：船司账单与运单自动匹配，付款前先暴露争议项。",
        "真正的 AI FMS 要在这六块之间闭环。范围更窄的，只能算单点工具，而不是管理系统。",
      ],
    },
    {
      heading: "看-算-写闭环",
      body: [
        "判断是否 AI 原生，最实际的标准是每个 Agent 能否做到看、算、写。看：无需固定模板即可读取邮件、PDF 或门户页面。算：套用运价、附加费、业务规则与利润逻辑。写：把结果写回业务系统——一张报价、一笔订舱、一份申报、一条账目。",
        "只会看（OCR）或只会写（RPA）的自动化，把最难的“决策”留给了操作人员。这正是 RPA 项目容易停滞的原因，也是 AI FMS 与脚本之间的分界线。",
      ],
    },
    {
      heading: "选型检查清单",
      body: [
        "别只问准确率，要问异常率：多少比例的单子需要人工介入，这个数字是在下降吗？再问：船司改版门户或单证换版式时系统怎么表现？每一次自动化动作是否可审计、可回退？",
        "然后看商业结构：是云平台订阅与按用量计费，还是永久授权加多年实施？能否从一条航线或一个分公司开始分阶段上线？从现有 TMS 迁移是否需要停业务？",
      ],
    },
  ],
  ctaTitle: "在您的航线上看看 AI FMS 怎么跑",
  ctaBody:
    "CargoNova 是 Deep Insights 打造的 AI FMS，7 位 AI 数字员工覆盖报价、订舱、单证、关务与结算。",
  ctaButton: "预约演示",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  moreLabel: "什么是 CargoNova",
  compareLabel: "CargoNova vs CargoWise",
  backLabel: "返回首页",
};

const contentTh: typeof contentEn = {
  eyebrow: "คู่มือหมวดหมู่ · ฟอร์เวิร์ดเดอร์ AI-Native",
  title: "ระบบ FMS (Freight Management System) และ AI FMS คืออะไร?",
  lede:
    "FMS ย่อมาจาก freight management system คือระบบบริหารงานขนส่งที่ฟอร์เวิร์ดเดอร์ใช้ทำงาน ส่วน AI FMS คือระบบบริหารงานขนส่งแบบ AI-Native ที่ AI Agent อ่านอีเมล คำนวณค่าระวาง จองระวาง ดึงข้อมูลเอกสาร และปิดงานให้เอง บทความนี้อธิบายนิยาม เปรียบเทียบระบบ FMS กับ TMS แบบเดิม และให้เช็กลิสต์สำหรับการเลือกระบบ",
  meta: "อ่าน 7 นาที · อัปเดต 11 สิงหาคม 2026",
  sections: [
    {
      heading: "นิยาม: ระบบ FMS และ AI FMS คืออะไรกันแน่",

      body: [
        "FMS คือระบบบริหารงานขนส่งที่ฟอร์เวิร์ดเดอร์ใช้ทำใบเสนอราคา จองระวาง จัดการเอกสาร พิธีการศุลกากร และการชำระเงิน ส่วน AI FMS คือระบบเดียวกันที่ถูกสร้างใหม่รอบ AI Agent ซอฟต์แวร์ทำงานเอง และถามคนเฉพาะตอนที่ต้องใช้ดุลพินิจ",
        "ความต่างนี้สำคัญ เพราะการเอา AI ไปแปะบนระบบเดิมมักได้แค่แชตบอตบนกระบวนการที่ยังทำมือเหมือนเดิม ซอฟต์แวร์ฟอร์เวิร์ดเดอร์แบบ AI-Native จะวางโมเดลไว้ในเส้นทางการทำงานจริง ทั้งอ่านอีเมลลูกค้า เทียบค่าระวาง กรอกการจอง อ่าน B/L และกระทบยอดใบแจ้งหนี้",
        "สรุปเป็นลำดับเดียว: ระบบ FMS (freight management system) คือระบบบริหารงานขนส่งที่ฟอร์เวิร์ดเดอร์ใช้ทำงาน, ระบบ AI FMS คือ FMS ที่ให้ AI Agent เป็นผู้ลงมือทำงาน, ส่วนแพลตฟอร์ม AI FMS ที่ให้บริการบนคลาวด์และออกแบบมาแบบนี้ตั้งแต่บรรทัดแรกคือ AI-native FMS CargoNova AI คือแพลตฟอร์ม AI-native FMS ในความหมายนี้",
      ],
    },
    {
      heading: "AI FMS เทียบกับ TMS / FMS แบบเดิม",
      table: [
        ["มิติ", "TMS / FMS แบบเดิม", "AI FMS"],
        ["การใช้งานหลัก", "คนกรอกฟอร์ม", "AI Agent ทำงาน คนกำกับดูแล"],
        ["งานเอกสาร", "คีย์มือหรือเทมเพลตตายตัว", "LLM + OCR อ่านได้ทุกรูปแบบ"],
        ["การเสนอราคา", "เปิดตารางค่าระวางเอง", "ใบเสนอราคาด้วย AI ภายในไม่กี่นาที"],
        ["รับมือการเปลี่ยนแปลง", "พังเมื่อรูปแบบเปลี่ยน", "ปรับตัวได้ และแจ้งเคสพิเศษ"],
        ["รูปแบบค่าใช้จ่าย", "ไลเซนส์ถาวร + ค่า implement", "Subscription แบบคลาวด์"],
        ["ระยะเวลา go-live", "6–18 เดือน", "ไม่กี่สัปดาห์ แบบ phased"],
      ],
    },
    {
      heading: "ระบบ FMS ต่างจาก TMS อย่างไร?",
      body: [
        "TMS (transport management system) ออกแบบมาสำหรับฝั่งผู้ส่งสินค้าและผู้ขนส่ง เช่น การวางแผนจัดรถ จัดเส้นทาง และติดตามการขนส่งภายในประเทศ ส่วนระบบ FMS ออกแบบมาสำหรับฟอร์เวิร์ดเดอร์ ทั้งการขนส่งระหว่างประเทศหลายช่วง ตารางค่าระวางและเซอร์ชาร์จของสายเรือ House/Master B/L ใบขนศุลกากร และกำไรต่อชิปเมนต์จากส่วนต่างราคาซื้อ-ขาย",
        "ฟอร์เวิร์ดเดอร์ที่ใช้ TMS จึงมักต้องสร้างระบบบริหารงานขนส่งครึ่งหนึ่งขึ้นมาใหม่ใน Excel เพราะโครงสร้างข้อมูลไม่ตรงกับงานฟอร์เวิร์ด แพลตฟอร์ม FMS จำลองไฟล์ชิปเมนต์ได้ตรงตั้งแต่ต้น และแพลตฟอร์ม AI FMS ไปไกลกว่านั้นด้วยการให้ AI Agent ทำงานทั้งไฟล์แบบ end-to-end",
      ],
    },

    {
      heading: "หกความสามารถหลักของ AI FMS",
      body: [
        "1. งานขายและการรับคำถาม: อ่าน จัดหมวด และส่งต่ออีเมลขาเข้าอัตโนมัติ 2. ค่าระวางและใบเสนอราคา: เทียบราคาสายเรือในเส้นทางแล้วออกใบเสนอราคาที่ส่งลูกค้าได้ทันที 3. การจองระวาง: ขอระวางและกระทบยอดใบยืนยันกับข้อมูลการจอง",
        "4. เอกสาร: แปลง B/L, packing list และใบแจ้งหนี้เป็นข้อมูลเชิงโครงสร้างพร้อมร่องรอยตรวจสอบ 5. ศุลกากรและการปฏิบัติตามกฎ: เติมข้อมูลใบขนล่วงหน้าและตรวจรายการควบคุม 6. การชำระเงิน: จับคู่ใบแจ้งหนี้สายเรือกับชิปเมนต์ และเปิดประเด็นข้อโต้แย้งก่อนจ่าย",
        "AI FMS ที่แท้จริงต้องครบวงจรทั้งหกส่วน หากแคบกว่านั้นถือเป็นเครื่องมือเฉพาะจุด ไม่ใช่ระบบบริหารงาน",
      ],
    },
    {
      heading: "วงจร เห็น–คำนวณ–เขียน",
      body: [
        "วิธีทดสอบว่าเป็น AI-Native จริงคือดูว่าแต่ละ Agent ทำได้ครบสามขั้นไหม เห็น: อ่านอีเมล PDF หรือหน้าเว็บพอร์ทัลได้โดยไม่ต้องมีเทมเพลตตายตัว คำนวณ: ใช้ค่าระวาง ค่าเซอร์ชาร์จ กฎธุรกิจ และมาร์จิ้น เขียน: ส่งผลลัพธ์กลับเข้าระบบ ทั้งใบเสนอราคา การจอง ใบขน หรือรายการบัญชี",
        "ระบบที่ทำได้แค่เห็น (OCR) หรือแค่เขียน (RPA) จะทิ้งส่วนที่ยากที่สุดคือการตัดสินใจไว้กับพนักงาน นี่คือเหตุผลที่โครงการ RPA มักหยุดนิ่ง และเป็นเส้นแบ่งระหว่าง AI FMS กับสคริปต์ธรรมดา",
      ],
    },
    {
      heading: "เช็กลิสต์ก่อนตัดสินใจ",
      body: [
        "อย่าถามแค่ความแม่นยำ ให้ถามอัตราเคสที่ต้องใช้คน: กี่เปอร์เซ็นต์ของงานที่ต้องเข้าไปแก้ และแนวโน้มลดลงหรือไม่ ถามด้วยว่าเมื่อสายเรือเปลี่ยนพอร์ทัลหรือรูปแบบเอกสาร ระบบทำงานอย่างไร และทุกการกระทำอัตโนมัติตรวจสอบย้อนหลังและย้อนกลับได้หรือเปล่า",
        "จากนั้นดูโครงสร้างเชิงพาณิชย์: เป็น subscription แบบคลาวด์และคิดตามการใช้งาน หรือเป็นไลเซนส์ถาวรบวกโครงการหลายปี เริ่ม go-live จากเส้นทางเดียวหรือสาขาเดียวได้ไหม และการย้ายจาก TMS เดิมต้องหยุดงานหรือไม่",
      ],
    },
  ],
  ctaTitle: "ดู AI FMS ทำงานจริงบนเส้นทางของคุณ",
  ctaBody:
    "CargoNova คือ AI FMS จาก Deep Insights พร้อม AI Agent 7 ตัวครอบคลุมใบเสนอราคา การจอง เอกสาร ศุลกากร และการชำระเงิน",
  ctaButton: "จองเดโม",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  moreLabel: "CargoNova คืออะไร",
  compareLabel: "CargoNova vs CargoWise",
  backLabel: "กลับหน้าแรก",
};

const highlightsEn = [
  { icon: Brain, label: "AI-native, not bolted on" },
  { icon: FileSearch, label: "Reads any document layout" },
  { icon: Gauge, label: "AI freight quotation in minutes" },
  { icon: Network, label: "End-to-end, six capability blocks" },
  { icon: ShieldCheck, label: "Auditable, reversible actions" },
  { icon: Boxes, label: "Phased go-live in weeks" },
];

const highlightsZh = [
  { icon: Brain, label: "AI 原生，而非事后加装" },
  { icon: FileSearch, label: "任意版式单证均可读取" },
  { icon: Gauge, label: "智能报价，分钟级出价" },
  { icon: Network, label: "六大模块端到端闭环" },
  { icon: ShieldCheck, label: "全程可审计、可回退" },
  { icon: Boxes, label: "数周分阶段上线" },
];

const highlightsTh = [
  { icon: Brain, label: "AI-Native ไม่ใช่แค่แปะเพิ่ม" },
  { icon: FileSearch, label: "อ่านเอกสารได้ทุกรูปแบบ" },
  { icon: Gauge, label: "ใบเสนอราคาด้วย AI ในไม่กี่นาที" },
  { icon: Network, label: "ครบวงจรหกความสามารถ" },
  { icon: ShieldCheck, label: "ตรวจสอบย้อนหลังได้" },
  { icon: Boxes, label: "go-live แบบ phased ในไม่กี่สัปดาห์" },
];

const BlogWhatIsAiFms = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const isTh = language === "th";
  const c = isZh ? contentZh : isTh ? contentTh : contentEn;
  const highlights = isZh ? highlightsZh : isTh ? highlightsTh : highlightsEn;
  const prefix = language === "en" ? "" : `/${language}`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="What is an AI FMS? | CargoNova"
        description="AI FMS explained: AI-native freight management systems, how they differ from legacy TMS, and how to evaluate one."
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero */}
        <section className="relative container mx-auto px-4 max-w-5xl pt-10 md:pt-16 pb-8 md:pb-12">
          <p className="text-xs md:text-sm font-semibold uppercase tracking-[0.15em] text-secondary mb-4">
            {c.eyebrow}
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-[1.2] bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent">
            {c.title}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
            {c.lede}
          </p>
          <p className="text-xs text-muted-foreground/80">{c.meta}</p>
        </section>

        {/* Highlights */}
        <section className="relative container mx-auto px-4 max-w-5xl pb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {highlights.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 rounded-2xl bg-card/80 backdrop-blur ring-1 ring-border/50 px-4 py-3 shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium text-foreground">{label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Article */}
        <article className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          {c.sections.map((section, idx) => (
            <section key={idx} className="mb-10 md:mb-12">
              <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 leading-snug">
                {section.heading}
              </h2>

              {"body" in section &&
                section.body?.map((p, i) => (
                  <p
                    key={i}
                    className="text-[15px] md:text-base leading-relaxed text-muted-foreground mb-4"
                  >
                    {p}
                  </p>
                ))}

              {"table" in section && section.table && (
                <div className="overflow-x-auto rounded-2xl ring-1 ring-border/60 bg-card/70 backdrop-blur">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-secondary/10 text-foreground">
                        {section.table[0]!.map((h) => (
                          <th
                            key={h}
                            className="text-left font-semibold px-4 py-3 border-b border-border/50"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.slice(1).map((row, r) => (
                        <tr key={r} className="odd:bg-background/50">
                          {row.map((cell, cIdx) => (
                            <td
                              key={cIdx}
                              className={`px-4 py-3 border-b border-border/40 align-top ${
                                cIdx === 0
                                  ? "font-medium text-foreground"
                                  : "text-muted-foreground"
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* CTA */}
          <div className="mt-10 rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
              {c.ctaTitle}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-5">{c.ctaBody}</p>
            <div className="flex flex-wrap gap-3">
              <a
                href={c.ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
              >
                {c.ctaButton}
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link
                to={`${prefix}/what-is-cargonova`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {c.moreLabel}
              </Link>
              <Link
                to={`${prefix}/compare/cargonova-vs-cargowise`}
                className="inline-flex items-center gap-2 rounded-full ring-1 ring-border/60 bg-card/70 backdrop-blur px-6 py-3 text-sm font-semibold text-foreground hover:ring-secondary/50 hover:text-secondary transition-colors"
              >
                {c.compareLabel}
              </Link>
            </div>
          </div>

          <div className="mt-8 text-sm">
            <Link
              to={prefix === "" ? "/" : prefix}
              className="text-secondary hover:underline inline-flex items-center gap-1"
            >
              ← {c.backLabel}
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogWhatIsAiFms;
